import { useCallback, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { addYears, subYears } from 'date-fns';
import { Reminders } from 'screens/admin/Reviews/ReviewProcessV2Intent/components/Reminders';
import { useGetTimezoneOffset } from 'screens/performanceReview/helpers';
import { getDateWithEndOfDay, getDateWithStartOfDay } from 'utils/helpers/dateHelpers';

import { Calendar } from 'assets/assetsV1/iconComponents';

import {
  BottomDrawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  primaryDrawerWidth,
} from 'uiToolkitV1/BottomDrawer';
import { Button } from 'uiToolkitV1/Button';
import { DatePicker } from 'uiToolkitV1/DatePicker';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

const COMPENSATION_STAGES = [
  {
    key: 'LAUNCH',
    label: 'Launch',
    description: 'Launched along with Anniversary Review',
    isEditable: false,
    defaultDate: new Date('2025-01-01'),
  },
  {
    key: 'PROPOSAL',
    label: 'Proposal',
    description: '31 Days total duration',
    isEditable: true,
    defaultDate: new Date('2025-02-10'),
  },
  {
    key: 'APPROVAL',
    label: 'Approval',
    description: '31 Days total duration',
    isEditable: true,
    defaultDate: new Date('2025-03-26'),
  },
  {
    key: 'PUBLISH',
    label: 'Publish',
    description: '31 Days total duration',
    isEditable: true,
    defaultDate: new Date('2025-04-20'),
  },
];

const TimezoneNote = () => {
  const formattedOffset = useGetTimezoneOffset();
  return (
    <Box flex={1} py="8px">
      <TextLabel bold>Note on Time Zone:</TextLabel>
      <TextLabel color="text.g2" mt="8px">
        {`Note: The start and end time for the reviewer submissions follows the Organisation's time zone ${formattedOffset}.
        Hence, all forms open at 00:00 hrs of the start date and all forms close at 23:59 hrs of the end date of that time zone.`}
      </TextLabel>
    </Box>
  );
};

const SingleTimelineSelection = ({ stage, displayNumber }) => {
  const [selectedDate, setSelectedDate] = useState(stage.defaultDate);
  const [reminders, setReminders] = useState([]);

  const minDate = subYears(getDateWithStartOfDay(new Date()), 5);
  const maxDate = addYears(getDateWithEndOfDay(new Date()), 5);

  const onReminderChange = useCallback(
    (newReminders) => {
      setReminders(
        newReminders.map((reminder) => ({
          ...reminder,
          compensationStage: stage.key,
        }))
      );
    },
    [stage.key]
  );

  return (
    <Flex flexDirection="column" p="10px 12px 0 12px" alignItems="stretch">
      <Flex justifyContent="space-between">
        <TextBodyMedium color="text.g2" width="240px">
          {displayNumber}. {stage.label} :
        </TextBodyMedium>
        <Flex flexDirection="column" flexGrow={1} mt={-3}>
          <DatePicker
            onChange={setSelectedDate}
            selectedDate={selectedDate}
            minDate={minDate}
            maxDate={maxDate}
            disabled={!stage.isEditable}
            customInputProps={{
              placeholder: 'Select Date',
              width: '100%',
              justifyContent: 'space-between !important',
              StartIcon: null,
              EndIcon: Calendar,
            }}
          />
          {!stage.isEditable && (
            <TextLabel color="text.g2" mt={1}>
              {stage.description}
            </TextLabel>
          )}
        </Flex>
      </Flex>

      {stage.isEditable && (
        <Flex alignSelf="flex-end" mt={2} width="100%" pl="3.5">
          <Reminders remindersSettings={reminders} onChange={onReminderChange} />
        </Flex>
      )}
    </Flex>
  );
};

export const CompensationTimelineBottomDrawer = ({ isOpen, onClose }) => {
  const handleSaveClick = () => {
    // TODO: Implement save functionality
    console.log('Save clicked');
    onClose();
  };

  return (
    <BottomDrawer isOpen={isOpen} onClose={onClose}>
      <DrawerContent width={`${primaryDrawerWidth}px`}>
        <DrawerHeader onClose={onClose}>
          <Flex p="12px" justifyContent="space-between" alignItems="center" w="100%">
            <TextBodyMedium bold>Timeline & reminders</TextBodyMedium>
          </Flex>
        </DrawerHeader>

        <DrawerBody>
          <Flex
            borderRadius="8px"
            flexDir="column"
            border="4px solid #E7EBEF"
            bg="white"
            pb="4"
            mx="1"
            h="100%"
            overflow="auto"
          >
            <Flex flexDir="column" py={2} px={3} gap={6}>
              <TimezoneNote />
            </Flex>

            <Flex flexDir="column" gap={4}>
              {COMPENSATION_STAGES.map((stage, index) => (
                <SingleTimelineSelection key={stage.key} stage={stage} displayNumber={index + 1} />
              ))}
            </Flex>
          </Flex>
        </DrawerBody>

        <DrawerFooter>
          <Flex gap="12px" justifyContent="space-between" w="100%">
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="filled" onClick={handleSaveClick}>
              Save
            </Button>
          </Flex>
        </DrawerFooter>
      </DrawerContent>
    </BottomDrawer>
  );
};
